import { Component } from '@theme/component';

/**
 * @typedef {Object} AxProductPushRefs
 * @property {HTMLElement} list
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 */

/**
 * @extends {Component<AxProductPushRefs>}
 */
class AxProductPush extends Component {
  requiredRefs = ['list'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {number | undefined} */
  #syncFrame;

  connectedCallback() {
    super.connectedCallback();

    const { list, previous, next } = this.refs;
    if (!previous && !next) return;

    list.addEventListener('scroll', this.#requestSync, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#requestSync);
    this.#resizeObserver.observe(list);
    this.#syncArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.list?.removeEventListener('scroll', this.#requestSync);
    this.#resizeObserver?.disconnect();
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
  }

  previous() {
    this.#scrollByTile(-1);
  }

  next() {
    this.#scrollByTile(1);
  }

  #scrollByTile(direction) {
    const { list } = this.refs;
    const tile = list.querySelector('.ax-product-push__tile');
    if (!tile) return;

    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    list.scrollBy({ left: direction * (tile.getBoundingClientRect().width + gap), behavior: 'smooth' });
  }

  #requestSync = () => {
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = requestAnimationFrame(() => this.#syncArrows());
  };

  #syncArrows() {
    const { list, previous, next } = this.refs;
    const maxScroll = list.scrollWidth - list.clientWidth;

    if (previous) previous.disabled = list.scrollLeft <= 1;
    if (next) next.disabled = list.scrollLeft >= maxScroll - 1;
  }
}

customElements.define('ax-product-push', AxProductPush);
